// frontend/src/components/formularios/EstadoSeguimiento.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Alert
} from '@mui/material';

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';

import {
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

const EstadoSeguimiento = ({ 
  historialSeguimiento = [], 
  reglasBloqueo = {},
  mostrarDetalle = false,
  tipo = 'cualitativo' // 'cualitativo' o 'cuantitativo'
}) => {
  const [estadoActual, setEstadoActual] = useState(null);
  const [expandido, setExpandido] = useState(mostrarDetalle);
  const [analisisProgreso, setAnalisisProgreso] = useState(null);
  
  useEffect(() => {
    calcularEstado();
    if (historialSeguimiento.length > 0) {
      analizarProgreso();
    }
  }, [historialSeguimiento, reglasBloqueo]);
  
  useEffect(() => {
    setExpandido(mostrarDetalle);
  }, [mostrarDetalle]);
  
  const calcularEstado = () => {
    if (!historialSeguimiento || historialSeguimiento.length === 0) {
      setEstadoActual({ 
        tipo: 'nuevo', 
        mensaje: 'Sin evaluaciones previas',
        progreso: 0
      });
      return;
    }
    
    const reglas = typeof reglasBloqueo === 'string' ? 
      JSON.parse(reglasBloqueo) : reglasBloqueo;
    
    // Verificar reglas de bloqueo
    if (reglas?.reglas) {
      for (const regla of reglas.reglas) {
        const bloqueado = verificarRegla(regla, historialSeguimiento);
        if (bloqueado) {
          setEstadoActual({
            tipo: 'bloqueado',
            mensaje: bloqueado.mensaje,
            requiereAnalisis: bloqueado.requiereAnalisis,
            detalles: bloqueado
          });
          return;
        }
      }
    }
    
    // Estado normal - calcular progreso
    const ultimaEvaluacion = historialSeguimiento[historialSeguimiento.length - 1];
    const progreso = calcularProgreso(historialSeguimiento);
    
    setEstadoActual({
      tipo: 'activo',
      mensaje: `${historialSeguimiento.length} sesión${historialSeguimiento.length > 1 ? 'es' : ''} completada${historialSeguimiento.length > 1 ? 's' : ''}`,
      progreso: progreso,
      ultimaEvaluacion: ultimaEvaluacion
    });
  };
  
  const verificarRegla = (regla, seguimientos) => {
    switch (regla.tipo) {
      case 'consecutivos_en_proceso':
        return verificarConsecutivosEnProceso(regla, seguimientos);
      case 'puntaje_consecutivo':
        return verificarPuntajeConsecutivo(regla, seguimientos);
      case 'tiempo_limite':
        return verificarTiempoLimite(regla, seguimientos);
      default:
        return null;
    }
  };
  
  const verificarConsecutivosEnProceso = (regla, seguimientos) => {
    const ultimosN = seguimientos.slice(-regla.valor);
    
    if (ultimosN.length === regla.valor) {
      const todosEnProceso = ultimosN.every(s => 
        s.respuestas?.estado_progreso === regla.valor_campo
      );
      
      if (todosEnProceso) {
        return {
          mensaje: regla.mensaje,
          requiereAnalisis: regla.requiere_observacion
        };
      }
    }
    return null;
  };
  
  const verificarPuntajeConsecutivo = (regla, seguimientos) => {
    for (const condicion of regla.condiciones) {
      const ultimosN = seguimientos.slice(-condicion.consecutivos);
      
      if (ultimosN.length === condicion.consecutivos) {
        const todosCumplen = ultimosN.every(s => {
          // Lógica para verificar puntajes
          const puntaje = calcularPuntajeSesion(s.respuestas);
          return puntaje === condicion.valor;
        });
        
        if (todosCumplen) {
          return {
            mensaje: condicion.mensaje,
            accion: condicion.accion
          };
        }
      }
    }
    return null;
  };
  
  const verificarTiempoLimite = (regla, seguimientos) => {
    if (seguimientos.length === 0) return null;
    
    const primeraSesion = seguimientos[0];
    const diasTranscurridos = Math.floor(
      (new Date() - new Date(primeraSesion.fecha_completado)) / (1000 * 60 * 60 * 24)
    );
    
    if (diasTranscurridos >= regla.dias) {
      return {
        mensaje: regla.mensaje
      };
    }
    return null;
  };
  
  const calcularPuntajeSesion = (respuestas) => {
    if (!respuestas?.objetivos_mensuales) return 0;
    
    const puntajes = Object.values(respuestas.objetivos_mensuales)
      .map(obj => (obj.ses1_puntaje + obj.ses2_puntaje) / 2)
      .filter(p => p > 0);
    
    if (puntajes.length === 0) return 0;
    
    const promedio = puntajes.reduce((a, b) => a + b, 0) / puntajes.length;
    return Math.round(promedio);
  };
  
  const calcularProgreso = (seguimientos) => {
    // Para formularios cualitativos
    if (tipo === 'cualitativo') {
      const objetivosLogrados = seguimientos.filter(s => 
        s.respuestas?.estado_progreso === 'logrado'
      ).length;
      return (objetivosLogrados / Math.max(seguimientos.length, 1)) * 100;
    }
    
    // Para formularios cuantitativos
    const ultimaSesion = seguimientos[seguimientos.length - 1];
    if (ultimaSesion?.respuestas?.objetivos_mensuales) {
      const evaluaciones = Object.values(ultimaSesion.respuestas.objetivos_mensuales);
      const logrados = evaluaciones.filter(e => e.observacion === 'Logrado').length;
      return (logrados / Math.max(evaluaciones.length, 1)) * 100;
    }
    
    return 0;
  };
  
  const analizarProgreso = () => {
    const analisis = {
      total_sesiones: historialSeguimiento.length,
      objetivos_trabajados: new Set(),
      tendencia: 'estable',
      alertas: []
    };
    
    // Analizar objetivos trabajados
    historialSeguimiento.forEach(sesion => {
      if (sesion.objetivos_seleccionados) {
        sesion.objetivos_seleccionados.forEach(obj => 
          analisis.objetivos_trabajados.add(obj)
        );
      }
    });
    
    // Calcular tendencia
    if (historialSeguimiento.length >= 3) {
      const ultimas3 = historialSeguimiento.slice(-3);
      const progresos = ultimas3.map(s => {
        if (tipo === 'cualitativo') {
          return s.respuestas?.estado_progreso === 'logrado' ? 1 : 0;
        } else {
          return calcularPuntajeSesion(s.respuestas);
        }
      });
      
      if (progresos[2] > progresos[0]) analisis.tendencia = 'mejorando';
      else if (progresos[2] < progresos[0]) analisis.tendencia = 'empeorando';
    }
    
    // Generar alertas
    const ultimasSesiones = historialSeguimiento.slice(-3);
    const todasEnProceso = ultimasSesiones.every(s => 
      s.respuestas?.estado_progreso === 'en_proceso'
    );
    
    if (todasEnProceso && ultimasSesiones.length >= 3) {
      analisis.alertas.push({
        tipo: 'warning',
        mensaje: 'Últimas 3 sesiones sin progreso significativo'
      });
    }
    
    setAnalisisProgreso(analisis);
  };
  
  const getColorEstado = () => {
    switch (estadoActual?.tipo) {
      case 'bloqueado': return 'error';
      case 'activo': return estadoActual.progreso >= 70 ? 'success' : 'primary';
      case 'nuevo': return 'info';
      default: return 'default';
    }
  };
  
  const getIconoEstado = () => {
    switch (estadoActual?.tipo) {
      case 'bloqueado': return <LockIcon />;
      case 'activo': return estadoActual.progreso >= 70 ? <CheckCircleIcon /> : <AssignmentIcon />;
      case 'nuevo': return <InfoIcon />;
      default: return <AssignmentIcon />;
    }
  };
  
  const getIconoTendencia = () => {
    switch (analisisProgreso?.tendencia) {
      case 'mejorando': return <TrendingUpIcon color="success" />;
      case 'empeorando': return <TrendingDownIcon color="error" />;
      default: return <RemoveIcon color="action" />;
    }
  };
  
  if (!estadoActual) return null;
  
  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Encabezado principal */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          {getIconoEstado()}
          <Typography variant="subtitle1" color={getColorEstado()}>
            Estado: {estadoActual.mensaje}
          </Typography>
          {analisisProgreso && (
            <Tooltip title={`Tendencia: ${analisisProgreso.tendencia}`}>
              {getIconoTendencia()}
            </Tooltip>
          )}
        </Box>
        
        {historialSeguimiento.length > 0 && (
          <IconButton
            size="small"
            onClick={() => setExpandido(!expandido)}
          >
            {expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>
      
      {/* Barra de progreso */}
      {estadoActual.tipo === 'activo' && (
        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="textSecondary">
              Progreso general
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {Math.round(estadoActual.progreso)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={estadoActual.progreso} 
            color={getColorEstado()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}
      
      {/* Alertas */}
      {analisisProgreso?.alertas?.map((alerta, index) => (
        <Alert 
          key={index} 
          severity={alerta.tipo} 
          sx={{ mt: 2 }}
          icon={<WarningIcon />}
        >
          {alerta.mensaje}
        </Alert>
      ))}
      
      {/* Detalles expandibles */}
      <Collapse in={expandido}>
        <Box mt={3}>
          {/* Resumen de estadísticas */}
          {analisisProgreso && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Total Sesiones
                    </Typography>
                    <Typography variant="h6">
                      {analisisProgreso.total_sesiones}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Objetivos Trabajados
                    </Typography>
                    <Typography variant="h6">
                      {analisisProgreso.objetivos_trabajados.size}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Tendencia
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getIconoTendencia()}
                      <Typography variant="body1" textTransform="capitalize">
                        {analisisProgreso.tendencia}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Última Evaluación
                    </Typography>
                    <Typography variant="body2">
                      {new Date(historialSeguimiento[historialSeguimiento.length - 1]?.fecha_completado).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          
          {/* Timeline de sesiones para formularios cualitativos */}
          {tipo === 'cualitativo' && historialSeguimiento.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Historial de Sesiones
              </Typography>
              <Timeline position="right">
                {historialSeguimiento.map((sesion, index) => (
                  <TimelineItem key={index}>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(sesion.fecha_completado).toLocaleDateString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot 
                        color={
                          sesion.respuestas?.estado_progreso === 'logrado' ? 'success' :
                          sesion.respuestas?.estado_progreso === 'en_proceso' ? 'warning' : 'grey'
                        }
                      />
                      {index < historialSeguimiento.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2">
                        Sesión {index + 1}
                      </Typography>
                      <Chip
                        size="small"
                        label={sesion.respuestas?.estado_progreso || 'Sin estado'}
                        color={
                          sesion.respuestas?.estado_progreso === 'logrado' ? 'success' :
                          sesion.respuestas?.estado_progreso === 'en_proceso' ? 'warning' : 'default'
                        }
                      />
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Box>
          )}
          
          {/* Tabla de evaluaciones para formularios cuantitativos */}
          {tipo === 'cuantitativo' && historialSeguimiento.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Mes</TableCell>
                    <TableCell align="center">Objetivos Evaluados</TableCell>
                    <TableCell align="center">Promedio General</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historialSeguimiento.map((sesion, index) => {
                    const evaluaciones = Object.values(sesion.respuestas?.objetivos_mensuales || {});
                    const promedio = evaluaciones.length > 0 ?
                      evaluaciones.reduce((sum, e) => sum + e.promedio, 0) / evaluaciones.length : 0;
                    
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          {sesion.respuestas?.mes_evaluacion || 'Sin mes'}
                        </TableCell>
                        <TableCell align="center">
                          {evaluaciones.length}
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            color={
                              promedio >= 3 ? 'success.main' :
                              promedio >= 2 ? 'warning.main' : 'error.main'
                            }
                            fontWeight="bold"
                          >
                            {promedio.toFixed(1)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              promedio >= 3 ? 'Logrado' :
                              promedio >= 2 ? 'En Proceso' : 'Reprogramación'
                            }
                            color={
                              promedio >= 3 ? 'success' :
                              promedio >= 2 ? 'warning' : 'error'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default EstadoSeguimiento;